import { useCallback, useEffect, useMemo, useState } from 'react'
import { TBookingType } from '../../interfaces'
import { IProduct, IProductVariant } from 'shopify-api-node'
import { TMainTrackTimeFilter, TPriceFilterOptions, TTimeFilter } from './interface'
import { useFirebase } from '../Firebase'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { convertTo12HourFormat, getProductHandle, getRaceHourFromSku, getStartTimeAndEndTime, isVariantAvailableForSale } from '../../utilities'

interface Props {
  date?: Date
  track: TBookingType
  allSelectable?: boolean
}

const useBookingSelection = ({ date, track, allSelectable }: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date>(date || new Date())
  const [loading, setLoading] = useState(false)
  const [timeFilter, setTimeFilter] = useState<TTimeFilter | TMainTrackTimeFilter>("MORNING")
  const [product, setProduct] = useState<IProduct | undefined>()
  const { db } = useFirebase()
  const productRef = useMemo(() => collection(db, "products"), [db]);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async (handle: string) => {
    setLoading(true)
    setError(null);
    try {
      console.log("handle", handle)
      const prodQuery = query(productRef, where("handle", "==", handle));
      const qs = await getDocs(prodQuery)
      console.log("qs", qs)
      if(!qs.empty && qs.docs.length > 0){
        const productData = qs.docs[0].data();
        console.log("productData", productData)
        setProduct(productData as IProduct)
      }
    } catch (error) {
      console.log(error)
      setError(error as Error);
    } finally {
      setLoading(false)
    }
  }, [productRef])

  const filterOptions: { title: string; subTitle?: string; active: boolean; filter: TPriceFilterOptions | TMainTrackTimeFilter | TTimeFilter }[] = useMemo(() => {

    const getTimeFilterLabel = (timeFilter: TMainTrackTimeFilter | TTimeFilter, track: TBookingType) => {
      const { startHour, endHour } = getStartTimeAndEndTime(timeFilter, track);

      return `${convertTo12HourFormat(startHour)} - ${convertTo12HourFormat(endHour)}`;
    };

    switch (track) {
      case "MAIN_TRACK":
        return [
          { title: "Morning", subTitle: getTimeFilterLabel("MORNING", track), active: timeFilter === "MORNING", filter: "MORNING" as TMainTrackTimeFilter },
          { title: "Midday", subTitle: getTimeFilterLabel("MIDDAY", track), active: timeFilter === "MIDDAY", filter: "MIDDAY" as TMainTrackTimeFilter },
          { title: "Evening", subTitle: getTimeFilterLabel("EVENING", track), active: timeFilter === "EVENING", filter: "EVENING" as TMainTrackTimeFilter },
          { title: "Late", subTitle: getTimeFilterLabel("LATE", track), active: timeFilter === "LATE", filter: "LATE" as TMainTrackTimeFilter },
        ];
      case "JUNIOR_TRACK":
        return [
          { title: "Morning", subTitle: getTimeFilterLabel("MORNING", track), active: timeFilter === "MORNING", filter: "MORNING" as TMainTrackTimeFilter },
          { title: "Midday", subTitle: getTimeFilterLabel("MIDDAY", track), active: timeFilter === "MIDDAY", filter: "MIDDAY" as TMainTrackTimeFilter },
          { title: "Evening", subTitle: getTimeFilterLabel("EVENING", track), active: timeFilter === "EVENING", filter: "EVENING" as TMainTrackTimeFilter },
        ];
      case "VR":
        return [
          { title: "Morning", subTitle: getTimeFilterLabel("MORNING", track), active: timeFilter === "MORNING", filter: "MORNING" as TTimeFilter },
          { title: "Afternoon", subTitle: getTimeFilterLabel("AFTERNOON", track), active: timeFilter === "AFTERNOON", filter: "AFTERNOON" as TTimeFilter },
          { title: "Evening", subTitle: getTimeFilterLabel("EVENING", track), active: timeFilter === "EVENING", filter: "EVENING" as TTimeFilter },
        ];
      case "BATTLE_KART":
        return [
          { title: "Morning", subTitle: getTimeFilterLabel("MORNING", track), active: timeFilter === "MORNING", filter: "MORNING" as TTimeFilter },
          { title: "Midday", subTitle: getTimeFilterLabel("MIDDAY", track), active: timeFilter === "MIDDAY", filter: "MIDDAY" as TTimeFilter },
          { title: "Evening", subTitle: getTimeFilterLabel("EVENING", track), active: timeFilter === "EVENING", filter: "EVENING" as TTimeFilter },
          { title: "Late", subTitle: getTimeFilterLabel("LATE", track), active: timeFilter === "LATE", filter: "LATE" as TTimeFilter },
        ];
      default:
        return [
          { title: "Morning", subTitle: getTimeFilterLabel("MORNING", track), active: timeFilter === "MORNING", filter: "MORNING" as TTimeFilter },
          { title: "Afternoon", subTitle: getTimeFilterLabel("AFTERNOON", track), active: timeFilter === "AFTERNOON", filter: "AFTERNOON" as TTimeFilter },
          { title: "Evening", subTitle: getTimeFilterLabel("EVENING", track), active: timeFilter === "EVENING", filter: "EVENING" as TTimeFilter },
        ];
    }
  }, [track, timeFilter]);

  const handleFilterOptionClick = useCallback((option: (typeof filterOptions)[number]) => () => {
    if (track === "MAIN_TRACK" || track === "JUNIOR_TRACK") {
      setTimeFilter(option.filter as TMainTrackTimeFilter)
    } else {
      setTimeFilter(option.filter as TTimeFilter)
    }
  }, [track])

  const filteredVariantsMap: Record<string, IProductVariant[]> = useMemo(() => {
    if(!product || !product.variants) return {}
    const map: Record<string, IProductVariant[]> = {};

    const filteredVariantsExceptTime = allSelectable ? product.variants :product.variants.filter(variant => isVariantAvailableForSale(variant))

    filterOptions.forEach(option => {
      const { startHour, endHour } = getStartTimeAndEndTime(option.filter as TMainTrackTimeFilter | TTimeFilter, track);
      const timeSlotVariants = filteredVariantsExceptTime.filter((variant) => {
        const variantHour = getRaceHourFromSku(variant?.sku);
        return variantHour >= startHour && variantHour < endHour;
      });
      map[option.filter] = timeSlotVariants;
    })
    return map
  }, [product, filterOptions, track, allSelectable])

  const filteredVariants: IProductVariant[] = useMemo(() => {
    return filteredVariantsMap[timeFilter as string] || []
  }, [filteredVariantsMap, timeFilter])

  const getTotalAvailableSeats = useCallback((filter: TPriceFilterOptions | TMainTrackTimeFilter | TTimeFilter) => {
    return filteredVariantsMap[filter as string]?.filter((variant) => variant.inventory_quantity > 0).length || 0
  },[filteredVariantsMap])

  const onDateChange = useCallback((date: Date) => {
    setSelectedDate(date)
    const handle = getProductHandle(date, track);
    handle && fetchData(handle)
  }, [track, fetchData])

  useEffect(() => {
    if (date) {
      setSelectedDate(date)
    }
  }, [date])

  useEffect(()=>{
    const handle = getProductHandle(selectedDate, track);
    handle && fetchData(handle)
  }, [selectedDate, track, fetchData])

  return { 
    loading, 
    error,
    selectedDate, 
    filterOptions, 
    filteredVariantsMap, 
    filteredVariants, 
    product,
    onDateChange, 
    handleFilterOptionClick, 
    getTotalAvailableSeats,
  }
}

export default useBookingSelection